// @flow
import JSZip from 'jszip';
import BluebirdPromise from 'bluebird';
import filesaver from 'file-saver';
import mimeTypes from 'mime'; // Went with this name to prevent conflicts

export const mimeMatchesSpecifier = (
  specifier: string,
  mime: string,
): boolean => mime.trim().toLowerCase() === specifier.trim().toLowerCase();

export const nameMatchesExtensionSpecifier = (
  specifier: string,
  name: string,
): boolean => new RegExp(`\\${specifier}$`, 'i').test(name);

export const mimeMatchesGlobSpecifier = (
  specifier: string,
  mime: string,
): boolean => new RegExp(`^${specifier.replace('/*', '')}/`, 'i').test(mime);

export const matchesSpecifierList = (
  specifierList: string,
  file: File,
): boolean => {
  const specifiers = specifierList
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length !== 0);

  if (specifiers.length === 0) return true;

  const extensionSpecifiers = specifiers.filter(
    (spec) => !!spec.match(/\.\w+$/),
  );
  const globSpecifiers = specifiers.filter(
    (spec) => !!spec.match(/^(video|audio|image)\/\*/),
  );
  const mimeSpecifiers = specifiers.filter(
    (spec) =>
      !extensionSpecifiers.includes(spec) && !globSpecifiers.includes(spec),
  );

  const mime = file.type;
  const { name } = file;

  return (
    (extensionSpecifiers.length > 0 &&
      extensionSpecifiers.some((spec) =>
        nameMatchesExtensionSpecifier(spec, name),
      )) ||
    (globSpecifiers.length > 0 &&
      globSpecifiers.some((spec) => mimeMatchesGlobSpecifier(spec, mime))) ||
    (mimeSpecifiers.length > 0 &&
      mimeSpecifiers.some((spec) => mimeMatchesSpecifier(spec, mime)))
  );
};

export const asText = (blob: Blob, encoding?: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      // we are only attempting to load as string, so that's the only thing this could be
      resolve(((reader.result: any): string));
    });

    reader.addEventListener('error', reject);

    reader.readAsText(blob, encoding);
  });

export const mimeSubtype = (mime: string): string => mime.split('/')[1];

// takes a list of blobs and a list of names and puts them in a zip
export const zipArchive = (
  files: $ReadOnlyArray<Blob>,
  fileFolderNames: $ReadOnlyArray<[string, string]>,
): BluebirdPromise<Blob> =>
  new BluebirdPromise((resolve, reject) => {
    const zip = new JSZip();
    fileFolderNames.forEach(([folderName, fileName], idx) => {
      zip.folder(folderName).file(fileName, files[idx]);
    });
    zip
      .generateAsync({ type: 'blob' })
      .then((blob) => {
        resolve(blob);
      })
      .catch(reject);
  });

export const downloadFile = (blob: Blob, fileName: string) => {
  const fileType = mimeTypes.getExtension(blob.type);
  if (fileType) {
    filesaver(blob, `${fileName}.${fileType}`);
    // eslint-disable-next-line no-console
  } else console.warn(`incompatible mime type ${blob.type}`);
};
